/**
 * pd-search-results.js — the search page, rendered from our own feed.
 *
 * Same API the search overlay uses (/api/storefront/product-search), same card markup
 * as the collection feed, so a product looks the same wherever it is found.
 *
 * Prices: the API returns the shop's base amount (DKK). Printing that with the page's
 * currency is wrong by the exchange rate, so the market price is fetched with the same
 * @inContext Storefront query the related row and the wishlist use, and the cached
 * amount is only the fallback.
 */
(function () {
  "use strict";

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  var CHECK =
    '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" ' +
    'stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';

  var GARAGE_KEY = "fitment_garage";
  var GARAGE_ACTIVE_KEY = "fitment_garage_active";

  /** The bike the customer is shopping for, or null. Same store the garage writes. */
  function activeVehicle() {
    try {
      var all = JSON.parse(localStorage.getItem(GARAGE_KEY) || "[]");
      if (!Array.isArray(all)) return null;
      var key = localStorage.getItem(GARAGE_ACTIVE_KEY) || "";
      if (!key) return null;
      return all.find(function (v) { return v.year + "|" + v.make + "|" + v.model === key; }) || null;
    } catch (e) { return null; }
  }

  function bikeLabel(v) {
    return [v.year, v.makeDisplay || v.make, v.modelDisplay || v.model].filter(Boolean).join(" ");
  }

  function ctxCurrency() {
    var el = document.querySelector("[data-pd-ctx]");
    return (el && el.dataset.currency) ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "DKK";
  }

  function money(amount, ccy) {
    var n = Number(amount);
    if (!isFinite(n)) return "";
    if (window.pdFormatMoney && (!ccy || ccy === window.pdMoneyCcy)) return window.pdFormatMoney(n);   // the product page's format (theme.liquid)
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "da-DK", {
        style: "currency", currency: ccy || ctxCurrency(),
      }).format(n);
    } catch (e) { return String(amount); }
  }

  var SF_QUERY =
    "query P($ids:[ID!]!,$country:CountryCode!) @inContext(country:$country){ nodes(ids:$ids){ ... on ProductVariant { id price{ amount currencyCode } compareAtPrice{ amount currencyCode } } } }";

  async function priceInMarket(items, data, shop) {
    var ctxEl = document.querySelector("[data-pd-ctx]");
    var country = ctxEl && ctxEl.dataset.country;
    var rows = items.filter(function (i) { return i.variantId; });
    if (!rows.length || !country || !data.storefrontToken) return null;
    var ids = [...new Set(rows.map(function (i) { return i.variantId; }))];
    var market = new Map(), ccy = null;
    try {
      for (var i = 0; i < ids.length; i += 200) {
        var res = await fetch("https://" + shop + "/api/" + (data.storefrontApiVersion || "2025-01") + "/graphql.json", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": data.storefrontToken },
          body: JSON.stringify({
            query: SF_QUERY,
            variables: {
              ids: ids.slice(i, i + 200).map(function (v) { return "gid://shopify/ProductVariant/" + v; }),
              country: country,
            },
          }),
        });
        if (!res.ok) continue;
        var body = await res.json();
        ((body && body.data && body.data.nodes) || []).forEach(function (n) {
          if (!n || !n.id || !n.price) return;
          var m = String(n.id).match(/(\d+)$/);
          if (m) market.set(m[1], {
            amount: Number(n.price.amount),
            currency: n.price.currencyCode,
            compareAt: n.compareAtPrice && n.compareAtPrice.amount != null ? Number(n.compareAtPrice.amount) : null,
          });
        });
      }
    } catch (e) { return null; }
    rows.forEach(function (r) {
      var mk = market.get(String(r.variantId));
      if (!mk || mk.amount == null) return;
      r.price = mk.amount; r.compareAt = mk.compareAt; ccy = ccy || mk.currency;
    });
    return ccy;
  }

  /* The same badge the feed cards carry, in the same corner: green when the part is
     confirmed for the bike, neutral for a universal one, amber when it still has to be
     checked. Without a bike there is nothing to claim, so there is no badge — which is
     what the search overlay does too. */
  function fitBadge(p, L, hasBike) {
    if (!hasBike) return "<span></span>";
    if (p.fits) return '<span class="pd-fit pd-fit--yes">' + CHECK + " " + esc(L.fits) + "</span>";
    // An original (OEM) part has no fitments but is not universal: its own neutral badge.
    if (p.original) return '<span class="pd-fit pd-fit--uni pd-fit--orig">' + esc(L.original) + "</span>";
    if (p.universal || p.isUniversal) return '<span class="pd-fit pd-fit--uni">' + esc(L.universal) + "</span>";
    return '<span class="pd-fit pd-fit--no">' + esc(L.checkFit) + "</span>";
  }

  function approvalLabel(p) {
    var a = p.approval;
    if (!a || !a.type) return "";
    return a.type === "TEILEGUTACHTEN" ? "TÜV" : a.type;
  }

  // Shopify CDN images at card size (the cards are ~160-240px wide): the API hands out the original file.
  function cardImg(u) { if (!u || (u.indexOf("cdn.shopify.com") < 0 && u.indexOf("/cdn/shop/") < 0)) return u; return u + (u.indexOf("?") >= 0 ? "&" : "?") + "width=480"; }
  function card(p, L, ccy, hasBike) {
    var url = p.url || (p.handle ? "/products/" + p.handle : "#");
    var off = p.compareAt && p.compareAt > p.price ? Math.round((1 - p.price / p.compareAt) * 100) : 0;
    var multi = (p.variantCount || 1) > 1;
    var art = p.image
      ? '<img src="' + esc(cardImg(p.image)) + '" alt="' + esc(p.title || "") + '" loading="lazy" decoding="async">'
      : '<div class="pd-thumb__art--placeholder"></div>';
    var appr = approvalLabel(p);
    var chips = appr ? '<span class="pd-tag__ece">' + CHECK + " " + esc(appr) + "</span>" : "";
    var outlet = p.outlet
      ? '<span class="pd-outlet">' + esc(L.outlet) + (off > 0 ? " – " + esc(L.save) + " " + off + "%" : "") + "</span>"
      : "";
    var backorder = p.stock === "backorder";
    return '<li class="pd-prow__item"><article class="pd-card pd-card--clean">' +
      '<a href="' + esc(url) + '" class="pd-thumb"><div class="pd-thumb__art">' + art + "</div>" +
      '<div class="pd-overlays">' + fitBadge(p, L, hasBike) +
      (off > 0 ? '<span class="pd-sale">-' + off + "%</span>" : "<span></span>") + "</div>" +
      (chips || outlet ? '<div class="pd-tag-bar"><span class="pd-tag-row">' + chips + "</span>" + outlet + "</div>" : "") +
      "</a>" +
      '<div class="pd-card__body">' +
      '<a href="' + esc(url) + '"><h3 class="pd-name">' + esc(p.title || "") + "</h3></a>" +
      (multi || !p.sku ? "" : '<span class="pd-sku">' + esc(p.sku) + "</span>") +
      (p.outletCondition ? '<span class="pd-cond">' + esc(p.outletCondition) + "</span>" : "") +
      '<span class="pd-stock ' + (p.available ? "pd-stock--in" : "pd-stock--out") + '"><i class="pd-dot"></i>' +
      esc(p.available ? L.inStock : L.outStock) + "</span>" +
      '<div class="pd-card__foot"><div class="pd-price">' +
      (off > 0 ? "<s>" + esc(money(p.compareAt, ccy)) + "</s>" : "") +
      "<strong>" + esc(money(p.price, ccy)) + "</strong></div></div>" +
      (multi
        ? '<a href="' + esc(url) + '" class="pd-add pd-add--block pd-add--select"><span>' + esc(L.select) + "</span></a>"
        : '<div class="pd-qty" data-pd-qty>' +
          '<button type="button" class="pd-qty__btn" data-pd-qty-dec aria-label="−">−</button>' +
          '<input class="pd-qty__in" type="number" inputmode="numeric" min="1" max="99" value="1">' +
          '<button type="button" class="pd-qty__btn" data-pd-qty-inc aria-label="+">+</button></div>' +
          '<button type="button" class="pd-add pd-add--block' + (backorder ? " pd-add--backorder" : "") +
          '" data-pd-add="' + esc(p.variantId || "") + '"' + (p.available && p.variantId ? "" : " disabled") +
          "><span>" + esc(L.add) + "</span></button>") +
      "</div></article></li>";
  }

  var WARN =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 9v4M12 17h.01"/>' +
    '<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/></svg>';
  var TICK =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.6" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

  /* The same two bands the search overlay uses, with the same words: a green one over the
     parts confirmed for the bike, an amber one over everything else. The merchant replaced
     a hairline "other options" divider with these because customers scrolled straight past
     it and bought parts that did not fit. A band spans the whole grid row. */
  function band(kind, title, sub) {
    return '<li class="pd-srch__band pd-srch__band--' + kind + '" role="presentation">' +
      '<span class="pd-srch__bandicon">' + (kind === "fit" ? TICK : WARN) + "</span>" +
      '<span class="pd-srch__bandtext"><span class="pd-srch__bandtitle">' + title + "</span>" +
      (sub ? '<span class="pd-srch__bandsub">' + esc(sub) + "</span>" : "") + "</span></li>";
  }

  function labels(root) {
    var d = root.dataset;
    return {
      add: d.addLabel || "Add to cart", select: d.selectLabel || "Select variant",
      inStock: d.instockLabel || "In stock", outStock: d.outstockLabel || "Out of stock",
      outlet: d.outletLabel || "Outlet", save: d.saveLabel || "save",
      products: d.productsLabel || "products",
      prev: d.prevLabel || "Prev", next: d.nextLabel || "Next",
      showing: d.showingLabel || "{from}–{to} of {total}",
      loading: d.loadingLabel || "Loading…",
      fits: d.fitsLabel || "Fits your bike",
      universal: d.universalLabel || "Universal",
      original: d.originalLabel || "Original",
      checkFit: d.checkfitLabel || "Check fitment",
      confirmedFor: d.confirmedFor || "Confirmed fit for",
      unconfirmedFor: d.unconfirmedFor || "Fit NOT confirmed for",
      unconfirmedSub: d.unconfirmedSub || "",
      noModelSpecific: d.noModelSpecific || "Other options",
    };
  }

  function wireCards(root) {
    if (root._wired) return;
    root._wired = true;
    root.addEventListener("click", function (ev) {
      var step = ev.target.closest("[data-pd-qty-dec], [data-pd-qty-inc]");
      if (step) {
        var input = step.parentElement.querySelector(".pd-qty__in");
        var v = parseInt(input.value, 10) || 1;
        input.value = Math.min(99, Math.max(1, v + (step.hasAttribute("data-pd-qty-inc") ? 1 : -1)));
        return;
      }
      var add = ev.target.closest("[data-pd-add]");
      if (!add || !add.dataset.pdAdd) return;
      var qty = add.closest(".pd-card").querySelector(".pd-qty__in");
      add.disabled = true;
      fetch("/cart/add.js", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: Number(add.dataset.pdAdd), quantity: parseInt(qty && qty.value, 10) || 1 }] }),
      }).finally(function () {
        add.disabled = false;
        document.dispatchEvent(new CustomEvent("pd:cart-updated"));
      });
    });
  }

  /** 1 … 4 5 [6] 7 8 … 42 — first, last, and a window around the current page. */
  function pageWindow(cur, pages) {
    var out = [], add = function (n) { if (n >= 1 && n <= pages && out.indexOf(n) === -1) out.push(n); };
    add(1); add(2);
    for (var n = cur - 2; n <= cur + 2; n++) add(n);
    add(pages - 1); add(pages);
    return out.sort(function (a, b) { return a - b; });
  }

  function renderPager(root, L, page, perPage, total, capped) {
    var nav = root.querySelector("[data-pd-search-pager]");
    if (!nav) return;
    var pages = Math.max(1, Math.ceil(total / perPage));
    if (pages < 2) { nav.hidden = true; nav.innerHTML = ""; return; }
    var from = (page - 1) * perPage + 1;
    var to = Math.min(total, page * perPage);
    var range = L.showing.replace("{from}", from).replace("{to}", to)
      .replace("{total}", (capped ? total + "+" : total));
    var btns = ['<button type="button" class="pd-pag__btn" data-pd-page="' + (page - 1) + '"' +
      (page === 1 ? " disabled" : "") + ">← " + esc(L.prev) + "</button>"];
    var win = pageWindow(page, pages), prev = 0;
    win.forEach(function (n) {
      if (n - prev > 1) btns.push('<span class="pd-pag__gap">…</span>');
      btns.push('<button type="button" class="pd-pag__btn' + (n === page ? " pd-pag__btn--current" : "") +
        '" data-pd-page="' + n + '"' + (n === page ? ' aria-current="page"' : "") + ">" + n + "</button>");
      prev = n;
    });
    btns.push('<button type="button" class="pd-pag__btn" data-pd-page="' + (page + 1) + '"' +
      (page === pages ? " disabled" : "") + ">" + esc(L.next) + " →</button>");
    nav.innerHTML = '<span class="pd-pag__range">' + esc(range) + "</span>" +
      '<div class="pd-pag__btns">' + btns.join("") + "</div>";
    nav.hidden = false;
  }

  /** The page belongs in the address bar: shareable, bookmarkable, and Back steps
      through the results instead of leaving the page. Page 1 carries no parameter. */
  function writeUrl(page, push) {
    var url = new URL(location.href);
    if (page > 1) url.searchParams.set("page", String(page));
    else url.searchParams.delete("page");
    if (url.toString() === location.href) return;
    if (push) history.pushState({ pdSearch: page }, "", url.toString());
    else history.replaceState({ pdSearch: page }, "", url.toString());
  }

  async function load(root, page) {
    var L = labels(root);
    var shop = root.dataset.shop;
    // A brand link is just ?vendor=Drag+Specialties — no duplicated q, no type. When q is
    // absent the brand name IS the query, which keeps the link working on the API version
    // that does not know `vendor` yet: there it degrades to the text search it used to do.
    var qs = new URLSearchParams(location.search);
    var term = qs.get("q") || qs.get("vendor") || "";
    var grid = root.querySelector("[data-pd-search-grid]");
    var countEl = root.querySelector("[data-pd-search-count]");
    var emptyEl = root.querySelector("[data-pd-search-empty]");
    if (!term.trim()) { emptyEl.hidden = false; emptyEl.textContent = ""; return; }

    var base = (root.dataset.apiBase || "https://pd-garage.fly.dev").replace(/\/$/, "");
    var url = new URL("/api/storefront/product-search", base);
    url.searchParams.set("shop", shop);
    url.searchParams.set("q", term);
    // The brands page links with ?vendor=, an exact brand filter. Passed through
    // untouched: an older API version ignores it and falls back to the text query.
    var vendor = qs.get("vendor");
    if (vendor) {
      url.searchParams.set("vendor", vendor);
      // Arriving from the brands page: the brand's own name is the heading and the
      // "Search results" eyebrow goes (a "Brand" eyebrow over it was noise — merchant 2026-09-23).
      var eye = root.querySelector("[data-pd-search-eyebrow]");
      var head = root.querySelector("[data-pd-search-heading]");
      if (eye) eye.hidden = true;
      if (head) head.textContent = vendor;
    }
    url.searchParams.set("page", page);
    url.searchParams.set("per_page", root.dataset.perPage || "24");
    // "Kun dele der passer": the same switch as the search overlay's, remembered in the same
    // localStorage key, so a choice made in one carries into the other. Merchant 2026-09-23: a
    // brand page with a bike chosen had no filter, sort or universal toggle at all.
    if (root._bike && root._fitOnly) url.searchParams.set("fit_only", "1");
    // With a bike chosen the API sorts parts that fit to the front and tells us how many
    // there are; without one it is an ordinary relevance sort and no bands are drawn.
    var bike = root._bike;
    if (bike) {
      url.searchParams.set("year", bike.year);
      url.searchParams.set("make", bike.make);
      url.searchParams.set("model", bike.model);
      if (bike.vehicleType) url.searchParams.set("vehicleType", bike.vehicleType);
    }

    var data;
    try {
      var res = await fetch(url.toString());
      if (!res.ok) throw new Error(res.status);
      data = await res.json();
    } catch (e) {
      var nav = root.querySelector("[data-pd-search-pager]");
      if (nav) nav.hidden = true;
      emptyEl.hidden = false;
      emptyEl.textContent = root.dataset.searchFailed || "";
      return;
    }

    var items = data.products || [];
    var ccy = (await priceInMarket(items, data, shop)) || ctxCurrency();

    // Each page stands on its own now, so the bands are worked out per page: a page can
    // be all fitting parts, all non-fitting, or straddle the boundary.
    grid.innerHTML = "";
    root._fitBand = false;
    root._warnBand = false;
    var bike = root._bike;
    var name = bike ? esc(bikeLabel(bike)) : "";
    // Merchant (2026-09-23): a page where EVERY product is universal — a brand like Muc-Off,
    // care products with no fitment at all — gets no band. "Check the fit before you buy"
    // cannot be followed on a product that has no fit, and the amber band contradicted the
    // UNIVERSAL badge on every card. A page with even one fitment-bearing product keeps both
    // bands exactly as before.
    var allUniversal = !!bike && items.length > 0 && items.every(function (p) {
      return !p.fits && (p.universal || p.isUniversal);
    });
    var html = items.map(function (p, i) {
      var out = "";
      if (bike && p.fits && !root._fitBand) {
        root._fitBand = true;
        out += band("fit", esc(L.confirmedFor) + " <strong>" + name + "</strong>", "");
      }
      // Before the first product outside the fitting group (the API flags it; the badge is the
      // fallback), so a "Passer din MC" card never sits under "pasform IKKE bekræftet".
      if (bike && !allUniversal && !(p.fits || p.fitGroup) && !root._warnBand) {
        root._warnBand = true;
        // Nothing fitted at all: say that, rather than "other options" under an empty section.
        // "no model-specific products" is about the whole result set, not this page.
        var first = (data.fitTotal === 0 || data.fitTotal == null) && i === 0 && !root._fitBand;
        out += band("warn",
          first ? esc(L.noModelSpecific) : esc(L.unconfirmedFor) + " <strong>" + name + "</strong>",
          L.unconfirmedSub);
      }
      return out + card(p, L, ccy, !!bike);
    }).join("");
    grid.innerHTML = html;

    var total = data.total || items.length;
    countEl.hidden = false;
    countEl.textContent = (data.totalCapped ? total + "+" : total) + " " + L.products;
    var fitBtn = root.querySelector("[data-pd-search-fitonly]");
    if (fitBtn) { fitBtn.hidden = !bike; fitBtn.classList.toggle("is-on", !!root._fitOnly); fitBtn.setAttribute("aria-pressed", root._fitOnly ? "true" : "false"); }
    emptyEl.hidden = items.length > 0;
    // The section renders the sentence with a __TERM__ placeholder because on a brand page
    // Liquid's search.terms is empty (the brand travels as ?vendor=), which read as
    // `Ingen resultater fundet for “”`. With the fit switch on, say what the switch did.
    if (!items.length) emptyEl.textContent = (bike && root._fitOnly && root.dataset.noFit) ? root.dataset.noFit : (root.dataset.noResults || "").replace("__TERM__", term);

    root._page = page;
    renderPager(root, L, page, Number(root.dataset.perPage || 24), total, !!data.totalCapped);
    wireCards(root);
    root.classList.add("is-ready");
    // A page change should start at the top of the results, not halfway down the old page.
    if (root._userPaged) {
      root._userPaged = false;
      root.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }

  function init(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = "1";
    root._bike = activeVehicle();
    root._fitBand = false;
    root._warnBand = false;
    try { root._fitOnly = localStorage.getItem("pdFitOnly") === "1"; } catch (e) { root._fitOnly = false; }
    root.addEventListener("click", function (ev) {
      var t = ev.target.closest("[data-pd-search-fitonly]"); if (!t) return;
      root._fitOnly = !root._fitOnly;
      try { localStorage.setItem("pdFitOnly", root._fitOnly ? "1" : "0"); } catch (e) {}
      root._page = 1; writeUrl(1, true); load(root, 1);
    });
    var startPage = Math.max(1, Number(new URLSearchParams(location.search).get("page") || 1));
    root._page = startPage;
    root.addEventListener("click", function (ev) {
      var btn = ev.target.closest("[data-pd-page]");
      if (!btn || btn.disabled) return;
      var n = Number(btn.dataset.pdPage);
      if (!n || n === root._page) return;
      root._userPaged = true;
      writeUrl(n, true);
      load(root, n);
    });
    // Back and forward move through the pages rather than off the page entirely.
    window.addEventListener("popstate", function () {
      var n = Math.max(1, Number(new URLSearchParams(location.search).get("page") || 1));
      if (n !== root._page) load(root, n);
    });
    load(root, startPage);
  }

  function boot() { document.querySelectorAll("[data-pd-search]").forEach(init); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  document.addEventListener("pd:navigated", boot);   // the page arrives by in-page navigation (pd-nav.js) too; init() is idempotent per root
  document.addEventListener("shopify:section:load", boot);
})();
